import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useToastStore } from "@/store/toastStore";
import {
  Box,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  ContentCopy,
  Restaurant,
  Euro,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
  AllergenChip,
} from "@/components/ui";
import {
  productService,
  ProductFilter,
  ProductType,
} from "@/services/product.service";
import { categoryService } from "@/services/category.service";

/**
 * Page de gestion des produits
 * Liste avec recherche, filtres et actions CRUD
 */
export function ProductsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = usePersistedState("products.searchQuery", "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = usePersistedState("products.categoryFilter", "all");
  const [typeFilter, setTypeFilter] = usePersistedState<ProductType | "all">("products.typeFilter", "all");
  const [availabilityFilter, setAvailabilityFilter] = usePersistedState<
    "all" | "available" | "unavailable"
  >("products.availabilityFilter", "all");
  const [vegetarianFilter, setVegetarianFilter] = usePersistedState<
    "all" | "vegetarian" | "non-vegetarian"
  >("products.vegetarianFilter", "all");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // État pour le dialog de confirmation de suppression
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    productId: string | null;
    productName: string;
  }>({
    open: false,
    productId: null,
    productName: "",
  });

  const filters: ProductFilter = {
    categoryId: categoryFilter !== "all" ? categoryFilter : undefined,
    type: typeFilter !== "all" ? typeFilter : undefined,
    availability: availabilityFilter,
    isVegetarian:
      vegetarianFilter === "all"
        ? undefined
        : vegetarianFilter === "vegetarian",
    search: debouncedSearch.trim() || undefined,
  };

  const {
    data: products = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["products", filters],
    queryFn: () => productService.getAll(filters),
  });

  // Allergènes par produit (déduits des ingrédients)
  const allergenQueries = useQueries({
    queries: products.map((p) => ({
      queryKey: ["product-allergens", p.id],
      queryFn: () => productService.getAllergens(p.id),
      enabled: products.length > 0,
    })),
  });
  const allergensByProductId = useMemo(() => {
    const map: Record<string, { id: string; name: string; code: string; icon: string | null }[]> = {};
    products.forEach((p, i) => {
      const data = allergenQueries[i]?.data;
      if (data?.length) map[p.id] = data;
    });
    return map;
  }, [products, allergenQueries]);

  // Récupérer les catégories pour le filtre
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => categoryService.getAll(),
  });

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour supprimer un produit
  const deleteMutation = useMutation({
    mutationFn: (id: string) => productService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      success("Produit supprimé avec succès");
      setDeleteConfirm({ open: false, productId: null, productName: "" });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la suppression du produit",
      );
    },
  });

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteConfirm({ open: true, productId: id, productName: name });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.productId) {
      deleteMutation.mutate(deleteConfirm.productId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, productId: null, productName: "" });
  };

  // Mutation pour dupliquer un produit
  const duplicateMutation = useMutation({
    mutationFn: (id: string) => productService.duplicate(id),
    onSuccess: (newProduct) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      success("Produit dupliqué avec succès");
      navigate(`/products/${newProduct.id}/edit`);
    },
    onError: (err: { message?: string; response?: { status?: number; data?: { message?: string } } }) => {
      const status = err?.response?.status;
      const is401 = status === 401;
      let msg: string;
      if (is401 || err?.message?.includes("Session expirée")) {
        msg = "Session expirée. Veuillez vous reconnecter.";
      } else if (status === 404) {
        msg = "Service de duplication indisponible (404). Vérifiez que le backend est à jour.";
      } else {
        msg = err?.response?.data?.message ?? err?.message ?? "Erreur lors de la duplication du produit";
        if (status) msg = `[${status}] ${msg}`;
      }
      showError(msg);
    },
  });

  const handleDuplicate = (id: string) => {
    duplicateMutation.mutate(id);
  };

  // Formater le prix
  const formatPrice = (price: number | string): string => {
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return `${numPrice.toFixed(2)}€`;
  };

  // Formater le type de produit
  const formatProductType = (type: ProductType): string => {
    const types: Record<ProductType, string> = {
      STARTER: "Entrée",
      MAIN_COURSE: "Plat",
      DESSERT: "Dessert",
      DRINK: "Boisson",
      SIDE: "Accompagnement / 100 % Légumes",
    };
    return types[type] || type;
  };

  if (isLoading) {
    return <LoadingSpinner message="Chargement des produits..." />;
  }

  if (queryError) {
    return (
      <ErrorMessage
        message="Erreur lors du chargement des produits."
        onRetry={() => queryClient.invalidateQueries({ queryKey: ["products"] })}
        retryLabel="Réessayer"
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Produits"
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate("/products/new")}
          >
            Nouveau Produit
          </Button>
        }
      />

      <FiltersCard>
          <InputField
            placeholder="Rechercher un produit..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            icon={<Search />}
            iconPosition="start"
            sx={{ flexGrow: 1, minWidth: 250, "& .MuiOutlinedInput-root": { height: 40 } }}
          />

          {/* Filtre par catégorie */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Catégorie"
            >
              <MenuItem value="all">Toutes</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filtre par type */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as ProductType | "all")
              }
              label="Type"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="STARTER">Entrée</MenuItem>
              <MenuItem value="MAIN_COURSE">Plat</MenuItem>
              <MenuItem value="DESSERT">Dessert</MenuItem>
              <MenuItem value="DRINK">Boisson</MenuItem>
              <MenuItem value="SIDE">Accompagnement / 100 % Légumes</MenuItem>
            </Select>
          </FormControl>

          {/* Filtre par disponibilité */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Disponibilité</InputLabel>
            <Select
              value={availabilityFilter}
              onChange={(e) =>
                setAvailabilityFilter(
                  e.target.value as "all" | "available" | "unavailable",
                )
              }
              label="Disponibilité"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="available">Disponibles</MenuItem>
              <MenuItem value="unavailable">Indisponibles</MenuItem>
            </Select>
          </FormControl>

          {/* Filtre végétarien */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Végétarien</InputLabel>
            <Select
              value={vegetarianFilter}
              onChange={(e) =>
                setVegetarianFilter(
                  e.target.value as "all" | "vegetarian" | "non-vegetarian",
                )
              }
              label="Végétarien"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="vegetarian">Végétarien</MenuItem>
            <MenuItem value="non-vegetarian">Non végétarien</MenuItem>
          </Select>
        </FormControl>
      </FiltersCard>

      {/* Tableau des produits */}
      {products.length === 0 ? (
        <EmptyState
          title="Aucun produit trouvé"
          description={
            debouncedSearch ||
            categoryFilter !== "all" ||
            typeFilter !== "all" ||
            availabilityFilter !== "all" ||
            vegetarianFilter !== "all"
              ? "Aucun produit ne correspond aux critères de recherche."
              : "Il n'y a aucun produit pour le moment. Créez-en un nouveau !"
          }
          action={
            !debouncedSearch &&
            categoryFilter === "all" &&
            typeFilter === "all" &&
            availabilityFilter === "all" &&
            vegetarianFilter === "all"
              ? {
                  label: "Créer un produit",
                  onClick: () => navigate("/products/new"),
                }
              : undefined
          }
        />
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Prix</TableCell>
                  <TableCell align="center">Végétarien</TableCell>
                  <TableCell align="center">Disponible</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Restaurant
                          sx={{ fontSize: 20, color: "text.secondary" }}
                        />
                        <Box sx={{ maxWidth: 320 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {product.name}
                          </Typography>
                          {product.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {product.description}
                            </Typography>
                          )}
                          {product.ingredients && product.ingredients.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={product.ingredients.map((pi) => pi.ingredient.name).join(", ")}
                            >
                              {(() => {
                                const list = product.ingredients.map((pi) => pi.ingredient.name).join(", ");
                                const maxLen = 45;
                                return list.length > maxLen ? `${list.slice(0, maxLen)}...` : list;
                              })()}
                            </Typography>
                          )}
                          {allergensByProductId[product.id]?.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              {allergensByProductId[product.id].map((a) => (
                                <AllergenChip
                                  key={a.id}
                                  label={a.icon ? `${a.icon} ${a.code}` : a.code}
                                  compact
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.category?.name || "N/A"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatProductType(product.type)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        <Euro sx={{ fontSize: 16, color: "text.secondary" }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatPrice(product.price)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {product.isVegetarian ? (
                        <CheckCircle
                          sx={{ color: "success.main", fontSize: 20 }}
                        />
                      ) : (
                        <Cancel sx={{ color: "text.disabled", fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          product.isAvailable ? "Disponible" : "Indisponible"
                        }
                        color={product.isAvailable ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/products/${product.id}/edit`)}
                        aria-label="Modifier"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(product.id)}
                        aria-label="Dupliquer"
                        disabled={duplicateMutation.isPending}
                      >
                        <ContentCopy />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteClick(product.id, product.name)
                        }
                        aria-label="Supprimer"
                        color="error"
                        disabled={deleteMutation.isPending}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Compteur de résultats */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              {products.length} produit{products.length > 1 ? "s" : ""} trouvé
              {products.length > 1 ? "s" : ""}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Supprimer le produit"
        message={`Êtes-vous sûr de vouloir supprimer le produit "${deleteConfirm.productName}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
